import React from "react";
import { Helmet } from "react-helmet-async";

interface OrganizationSchemaProps {
  type?: "Organization" | "LocalBusiness" | "SportsActivityLocation";
}

export const OrganizationSchema: React.FC<OrganizationSchemaProps> = ({
  type = "Organization",
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": type,
    name: "Malaeb",
    alternateName: "ملاعب",
    url: "https://malaebapp.com",
    logo: "https://malaebapp.com/images/logo.png",
    description:
      "Malaeb App #1 Booking application in MENA Region. Book a sports pitch or facility around you in Bahrain, Saudi Arabia, Kuwait, United Arab Emirates, and Oman.",
    email: "hello@malaebapp.com",
    telephone: "+97377377382",
    address: {
      "@type": "PostalAddress",
      addressCountry: "BH",
      addressRegion: "Bahrain",
    },
    sameAs: [
      "https://www.facebook.com/malaebapp",
      "https://www.instagram.com/malaebapp/",
      "https://www.linkedin.com/company/malaeb/",
      "https://www.tiktok.com/@malaebapp",
      "https://www.snapchat.com/add/malaebappbh",
    ],
    areaServed: [
      {
        "@type": "Country",
        name: "Bahrain",
      },
      {
        "@type": "Country",
        name: "Saudi Arabia",
      },
      {
        "@type": "Country",
        name: "United Arab Emirates",
      },
      {
        "@type": "Country",
        name: "Kuwait",
      },
      {
        "@type": "Country",
        name: "Oman",
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export const WebsiteSchema: React.FC = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Malaeb",
    alternateName: "ملاعب",
    url: "https://malaebapp.com",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://malaebapp.com/list?search={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface BreadcrumbSchemaProps {
  items: Array<{
    name: string;
    url: string;
  }>;
}

export const BreadcrumbSchema: React.FC<BreadcrumbSchemaProps> = ({
  items,
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface StadiumSchemaProps {
  stadium: {
    title: string;
    description?: string;
    address?: string;
    rating?: number;
    reviewCount?: number;
    images?: Array<{ image?: string }>;
    slug?: string;
    id?: string | number;
    phone?: string;
    price?: number;
  };
}

export const StadiumSchema: React.FC<StadiumSchemaProps> = ({ stadium }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    name: stadium.title,
    description: stadium.description || `Book ${stadium.title} on Malaeb`,
    url: `https://malaebapp.com/${stadium.slug || stadium.id}`,
    image:
      stadium.images?.[0]?.image || "https://malaebapp.com/images/logo.png",
    ...(stadium.address && {
      address: {
        "@type": "PostalAddress",
        streetAddress: stadium.address,
      },
    }),
    ...(stadium.phone && {
      telephone: stadium.phone,
    }),
    ...(stadium.rating && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: stadium.rating,
        ...(stadium.reviewCount && { reviewCount: stadium.reviewCount }),
      },
    }),
    ...(stadium.price && {
      priceRange: `${stadium.price} BHD`,
    }),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

interface FAQSchemaProps {
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

export const FAQSchema: React.FC<FAQSchemaProps> = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
